import React from 'react';
import { TFunction } from 'i18next';
import { MRT_ColumnDef } from 'material-react-table';
import { CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';
import { Tooltip } from '@mui/material';
import {
  renderFileReceivedTimeStampCell,
  createInsuredLenderNameRenderer,
  renderDefaultCell,
  renderEpipProcessingStatusCell,
  renderLoanStatusCell,
  renderCmhcAccountNumberCell,
  borrowerNameRenderer,
  renderDateCell,
  renderRemark,
  renderLoanRecordStatusCell
} from './vriCellRenderer';
import { PortfolioCancelledLoan, PortfolioLoanRecord } from './types';
import styles from './styles.module.scss';
import { renderDataTestId } from '../../../HbtPortfolioManagementTable/vriTableColumns';

export const createPortfolioColumns = (
  isCancelled: boolean,
  t: TFunction,
  language: string
): MRT_ColumnDef<PortfolioCancelledLoan | PortfolioLoanRecord>[] => {
  const insuredLenderNameRenderer = createInsuredLenderNameRenderer(language);
  const epipProcessingStatusCodeRenderer = renderEpipProcessingStatusCell(t);
  const LoanStatusCellRenderer = renderLoanStatusCell(t);
  const cmhcAccountNumberCellRenderer = renderCmhcAccountNumberCell(t);
  const LoanRecordStatusCellRenderer = renderLoanRecordStatusCell(t);

  if (!isCancelled) {
    return [
      {
        accessorKey: 'fileReceivedTimestamp' as const,
        header: t('Request-Receipt-Date'),
        Cell: renderFileReceivedTimeStampCell,
        enableSorting: true,
        size: language === 'en' ? 240 : 320,
        muiTableHeadCellProps: renderDataTestId('fileReceivedTimestamp')
      },
      {
        accessorKey: 'lenderLoanNumber' as const,
        header: t('lender-reference-number'),
        Cell: renderDefaultCell,
        enableSorting: true,
        size: 320,
        muiTableHeadCellProps: renderDataTestId('lenderLoanNumber')
      },
      {
        accessorKey: 'loanStatusCode' as const,
        header: t('Loan-Status'),
        Cell: LoanStatusCellRenderer,
        enableSorting: true,
        size: 240,
        muiTableHeadCellProps: renderDataTestId('loanStatusCode')
      },
      {
        accessorKey: 'lowRatioCmhcAccountNumber' as const,
        header: t('cmhc-low-ratio-account-number'),
        muiTableBodyCellProps(props: { cell: { getValue: () => any } }) {
          return {
            'aria-label':
              language === 'en'
                ? `CMHC Low Ratio Account Number ${props.cell.getValue()}`
                : `Caractéristiques du ratio faible CMHC ${props.cell.getValue()}`
          };
        },
        Cell: cmhcAccountNumberCellRenderer,
        enableSorting: true,
        size: 335,
        muiTableHeadCellProps: renderDataTestId('lowRatioCmhcAccountNumber')
      },
      {
        accessorKey: 'propertyAddress' as const,
        header: t('property-address'),
        Header: ({ column }: any) => {
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <div>{column.columnDef?.header}</div>
              <Tooltip
                title={t('Property-Address-Tool-Tip-Text')}
                placement="top-start"
                arrow
                slotProps={{
                  tooltip: {
                    className: styles.loansInventoryToolTip
                  },
                  arrow: {
                    className: styles.tooltipArrow
                  }
                }}
              >
                <div
                  style={{
                    display: 'inline-block',
                    marginLeft: '8px'
                  }}
                >
                  <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
                </div>
              </Tooltip>
            </div>
          );
        },
        Cell: renderDefaultCell,
        enableSorting: true,
        size: language === 'en' ? 235 : 275,
        muiTableHeadCellProps: renderDataTestId('propertyAddress')
      },
      {
        accessorKey: 'portfolioId' as const,
        header: t('portfolio-id'),
        Cell: renderDefaultCell,
        enableSorting: true,
        size: language === 'en' ? 170 : 210,
        muiTableHeadCellProps: renderDataTestId('portfolioId')
      },
      {
        accessorKey: 'epipProcessingStatusCode' as const,
        header: t('Portfolio-ID-Status'),
        Cell: epipProcessingStatusCodeRenderer,
        enableSorting: false,
        size: 300,
        muiTableHeadCellProps: renderDataTestId('epipProcessingStatusCode')
      },
      {
        accessorKey: 'borrower' as const,
        header: t('Borrower-Name'),
        Cell: borrowerNameRenderer(),
        enableSorting: true,
        size: 240,
        muiTableHeadCellProps: renderDataTestId('borrower')
      },
      {
        accessorKey: 'borrowerDateOfBirth' as const,
        header: t('Borrower-Date-of-Birth'),
        Cell: renderDateCell,
        enableSorting: true,
        size: 335,
        muiTableHeadCellProps: renderDataTestId('borrowerDateOfBirth')
      },
      {
        accessorKey: 'coBorrower' as const,
        header: t('Co-borrower-Name'),
        Cell: borrowerNameRenderer(),
        enableSorting: true,
        size: 240,
        muiTableHeadCellProps: renderDataTestId('coBorrower')
      },
      {
        accessorKey: 'coBorrowerDateOfBirth' as const,
        header: t('Co-borrower-Date-of-Birth'),
        Cell: renderDateCell,
        enableSorting: true,
        size: 335,
        muiTableHeadCellProps: renderDataTestId('coBorrowerDateOfBirth')
      },
      {
        accessorKey: 'insuredLenderName' as const,
        header: t('Insured-Approved-Lender-Name'),
        Cell: insuredLenderNameRenderer,
        enableSorting: true,
        size: 320,
        muiTableHeadCellProps: renderDataTestId('insuredLenderName')
      },
      {
        accessorKey: 'underwritingLenderName' as const,
        header: t('Underwriting-Approved-Lender-Name'),
        Cell: insuredLenderNameRenderer,
        enableSorting: true,
        size: language === 'en' ? 320 : 300,
        muiTableHeadCellProps: renderDataTestId('underwritingLenderName')
      },
      {
        accessorKey: 'latestRemark' as const,
        header: t('Remarks'),
        Header: ({ column }: any) => {
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <div>{column.columnDef?.header}</div>
              <Tooltip
                title={t('Remarks-Tooltip')}
                placement="top-start"
                arrow
                slotProps={{
                  tooltip: {
                    className: styles.loansInventoryToolTip
                  },
                  arrow: {
                    className: styles.tooltipArrow
                  }
                }}
              >
                <div
                  style={{
                    display: 'inline-block',
                    marginLeft: '8px'
                  }}
                >
                  <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
                </div>
              </Tooltip>
            </div>
          );
        },
        muiTableBodyCellProps(props) {
          return {
            'aria-label':
              language === 'en' ? `remarks ${props.row.id}` : `remarques ${props.row.id}`
          };
        },
        Cell: renderRemark,
        enableSorting: false,
        size: 200,
        muiTableHeadCellProps: renderDataTestId('latestRemark')
      }
    ];
  }

  return [
    {
      accessorKey: 'fileReceivedTimestamp' as const,
      header: t('Request-Receipt-Date'),
      Cell: renderFileReceivedTimeStampCell,
      enableSorting: true,
      size: language === 'en' ? 240 : 320,
      muiTableHeadCellProps: renderDataTestId('fileReceivedTimestamp')
    },
    {
      accessorKey: 'cmhcAccountNumber' as const,
      header: t('cmhc-account-number'),
      muiTableBodyCellProps(props: { cell: { getValue: () => any } }) {
        return {
          'aria-label':
            language === 'en'
              ? `CMHC Account Number ${props.cell.getValue()}`
              : `Caractéristiques faible CMHC ${props.cell.getValue()}`
        };
      },
      Cell: cmhcAccountNumberCellRenderer,
      enableSorting: true,
      size: 300,
      muiTableHeadCellProps: renderDataTestId('cmhcAccountNumber')
    },
    {
      accessorKey: 'lenderLoanNumber' as const,
      header: t('lender-reference-number'),
      Cell: renderDefaultCell,
      enableSorting: true,
      size: 320,
      muiTableHeadCellProps: renderDataTestId('lenderLoanNumber')
    },
    {
      accessorKey: 'loanEditStatusCode' as const,
      header: t('Loan-Status'),
      Cell: LoanRecordStatusCellRenderer,
      enableSorting: true,
      size: 240,
      muiTableHeadCellProps: renderDataTestId('loanEditStatusCode')
    },
    {
      accessorKey: 'portfolioId' as const,
      header: t('portfolio-id'),
      Cell: renderDefaultCell,
      enableSorting: true,
      size: language === 'en' ? 170 : 210,
      muiTableHeadCellProps: renderDataTestId('portfolioId')
    },
    {
      accessorKey: 'epipProcessingStatusCode' as const,
      header: t('Portfolio-ID-Status'),
      Cell: epipProcessingStatusCodeRenderer,
      enableSorting: false,
      size: 300,
      muiTableHeadCellProps: renderDataTestId('epipProcessingStatusCode')
    },
    {
      accessorKey: 'insuredLenderName' as const,
      header: t('Insured-Approved-Lender-Name'),
      Cell: insuredLenderNameRenderer,
      enableSorting: true,
      size: 320,
      muiTableHeadCellProps: renderDataTestId('insuredLenderName')
    },
    {
      accessorKey: 'latestRemark' as const,
      header: t('Remarks'),
      Header: ({ column }: any) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <div>{column.columnDef?.header}</div>
            <Tooltip
              title={t('Remarks-Tooltip')}
              placement="top-start"
              arrow
              slotProps={{
                tooltip: {
                  className: styles.loansInventoryToolTip
                },
                arrow: {
                  className: styles.tooltipArrow
                }
              }}
            >
              <div
                style={{
                  display: 'inline-block',
                  marginLeft: '8px'
                }}
              >
                <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
              </div>
            </Tooltip>
          </div>
        );
      },
      muiTableBodyCellProps(props) {
        return {
          'aria-label': language === 'en' ? `remarks ${props.row.id}` : `remarques ${props.row.id}`
        };
      },
      Cell: renderRemark,
      enableSorting: false,
      size: 200,
      muiTableHeadCellProps: renderDataTestId('latestRemark')
    }
  ];
};
